import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Search, MapPin, Briefcase, BarChart3, Clock, X, Loader2,
  AlertCircle, Building2, CheckCircle2, Layers, ShieldCheck, Info, Users, Globe, Eye, Mail, FileText,
} from "lucide-react";

const POPULAR = ["Software Engineer", "Data Analyst", "Product Manager", "DevOps Engineer", "UI/UX Designer"];
const EXPERIENCE_OPTS = [
  { value: "", label: "Any experience" },
  { value: "0-2", label: "0–2 years" },
  { value: "2-5", label: "2–5 years" },
  { value: "5+", label: "5+ years" },
];

function postedLabel(days) {
  if (days <= 1) return "1 day ago";
  if (days < 7) return `${days} days ago`;
  return `${Math.round(days / 7)} week${days >= 14 ? "s" : ""} ago`;
}

function expLabel(job) {
  if (job.exp_min === 0 && job.exp_max >= 99) return "Any experience";
  return `${job.exp_min}–${job.exp_max} years`;
}

function CompanyLogo({ job, size = "w-10 h-10 text-base" }) {
  const [failed, setFailed] = useState(false);
  const src = job.domain ? `https://www.google.com/s2/favicons?domain=${job.domain}&sz=64` : null;
  if (!src || failed) {
    return (
      <div className={`${size} rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 font-bold shrink-0`}>
        {(job.company || "?")[0].toUpperCase()}
      </div>
    );
  }
  return <img src={src} alt={job.company} onError={() => setFailed(true)}
    className={`${size} rounded-lg object-contain bg-white border border-slate-200 p-1 shrink-0`} />;
}

function ApplyModal({ job, onClose }) {
  const { quota, refreshUser } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [missing, setMissing] = useState([]);
  const [validating, setValidating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [rendered, setRendered] = useState({ subject: "", body: "" });
  const [resume, setResume] = useState({ loading: true, filename: null, hasResume: false });
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  const [resumeLoading, setResumeLoading] = useState(false);

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      const p = data.profile || {};
      setResume({ loading: false, filename: p.resume_filename, hasResume: !!p.resume_storage_path });
    }).catch(() => setResume({ loading: false, filename: null, hasResume: false }));
  }, []);

  useEffect(() => {
    api.get("/templates").then(({ data }) => {
      setTemplates(data.items);
      const pick = data.last_selected_id && data.items.find((t) => t.id === data.last_selected_id)
        ? data.last_selected_id
        : (data.items.find((t) => t.is_default)?.id || data.items[0]?.id || null);
      setTemplateId(pick);
    }).catch(() => toast.error("Could not load email templates."));
  }, []);

  useEffect(() => {
    if (!templateId) return;
    setValidating(true);
    api.post("/templates/render", {
      template_id: templateId,
      context: { company: job.company, job_title: job.title, recruiter_name: "the hiring team" },
    }).then(({ data }) => {
      setMissing(data.missing_fields || []);
      setRendered({ subject: data.subject || "", body: data.body || "" });
    }).catch(() => setMissing([]))
      .finally(() => setValidating(false));
  }, [templateId, job]);

  const previewResume = async () => {
    setResumeLoading(true);
    try {
      const res = await api.get("/profile/resume/download", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch {
      toast.error("Could not open resume.");
    } finally {
      setResumeLoading(false);
    }
  };

  const balance = quota?.is_pro ? "Unlimited" : `${quota?.remaining ?? 0} left`;
  const canSend = !missing.length && resume.hasResume && !validating && !confirming && !!templateId;

  const confirm = async () => {
    if (!canSend) return;
    setConfirming(true);
    try {
      await api.post("/jobs/apply", { job_id: job.id, template_id: templateId });
      await refreshUser();
      toast.success(`Application queued for ${job.title} at ${job.company}.`);
      onClose(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 402) {
        toast.error("No email credits left. Please upgrade to Pro.");
      } else if (detail?.error === "missing_fields") {
        setMissing(detail.fields || []);
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="apply-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <h2 className="text-lg font-bold text-slate-900">Apply to selected matches</h2>
          <button onClick={() => onClose(false)} data-testid="apply-close" className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Selected batch Network</span>
              <span className="font-semibold text-sky-600">1</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Email Credits to be Used</span>
              <span className="font-semibold text-sky-600">1</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Balance Email Credit</span>
              <span className="font-semibold text-sky-600" data-testid="apply-credit-balance">{balance}</span>
            </div>
          </div>

          {/* Resume attachment status */}
          {!resume.loading && (resume.hasResume ? (
            <div data-testid="apply-resume-attached" className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-2.5">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-emerald-800">Resume attached</p>
                <p className="text-xs text-emerald-700 truncate">{resume.filename}</p>
              </div>
              <button data-testid="apply-preview-resume" onClick={previewResume} disabled={resumeLoading}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 shrink-0">
                {resumeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview
              </button>
            </div>
          ) : (
            <div data-testid="apply-resume-missing" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex gap-2.5">
              <AlertCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-rose-700">No resume on file</p>
                <p className="text-xs text-rose-600 mt-1">A resume must be attached to every application. Upload one in Profile &amp; Resume to continue.</p>
              </div>
            </div>
          ))}

          <div>
            <label className="text-sm text-slate-600">Select Email Template</label>
            <select
              data-testid="apply-template-select"
              value={templateId || ""}
              onChange={(e) => setTemplateId(e.target.value)}
              className="mt-1.5 w-full h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/30"
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.category ? ` — ${t.category}` : ""}{t.is_default ? " (Recommended)" : ""}</option>
              ))}
            </select>
            <button data-testid="apply-preview-email" onClick={() => setShowEmailPreview((v) => !v)}
              disabled={validating || !rendered.body}
              className="mt-2 text-xs font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1 disabled:opacity-50">
              <Mail className="w-3.5 h-3.5" /> {showEmailPreview ? "Hide email preview" : "Preview email"}
            </button>
          </div>

          {showEmailPreview && rendered.body && (
            <div data-testid="apply-email-preview" className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-slate-200 bg-white">
                <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Subject</p>
                <p className="text-sm text-slate-800 font-medium">{rendered.subject}</p>
              </div>
              <div className="px-4 py-3 max-h-56 overflow-y-auto">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{rendered.body}</p>
                {resume.hasResume && (
                  <p className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> Attachment: {resume.filename}
                  </p>
                )}
              </div>
            </div>
          )}

          {validating ? (
            <div className="flex items-center gap-2 text-xs text-slate-400">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Validating template fields…
            </div>
          ) : missing.length > 0 ? (
            <div data-testid="apply-missing-error" className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex gap-2.5">
              <AlertCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-rose-700">
                  Required fields missing for this template: {missing.map((m) => m.label).join(", ")}.
                </p>
                <p className="text-xs text-rose-600 mt-1">Please complete these fields in your profile or choose another template.</p>
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 sticky bottom-0 bg-white">
          <Button variant="ghost" onClick={() => onClose(false)} data-testid="apply-cancel" className="text-slate-600">
            Cancel
          </Button>
          <Button
            data-testid="apply-confirm"
            onClick={confirm}
            disabled={!canSend}
            className="bg-amber-500 text-black hover:bg-amber-400 font-semibold px-8"
          >
            {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function Discover() {
  const [draft, setDraft] = useState({ q: "", company: "", location: "", experience: "", work_mode: "" });
  const [applied, setApplied] = useState({ q: "", company: "", location: "", experience: "", work_mode: "" });
  const [sort, setSort] = useState("recent");
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ locations: [], work_modes: [] });
  const [savedCompanies, setSavedCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [employer, setEmployer] = useState(null);
  const [employerOpen, setEmployerOpen] = useState(false);
  const [employerLoading, setEmployerLoading] = useState(false);

  const openEmployer = async (company) => {
    setEmployerOpen(true);
    setEmployerLoading(true);
    try {
      const { data } = await api.get("/companies/employer", { params: { company } });
      setEmployer(data);
    } catch {
      setEmployer({ name: company });
    } finally {
      setEmployerLoading(false);
    }
  };

  useEffect(() => {
    api.get("/jobs/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/companies/saved").then((r) => setSavedCompanies(r.data.items || [])).catch(() => {});
  }, []);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/jobs", { params: { ...applied, sort, page_size: 50 } });
      setJobs(data.items);
      setTotal(data.total);
      if (data.items.length && !data.items.find((j) => j.id === selectedId)) {
        setSelectedId(data.items[0].id);
      }
      if (!data.items.length) { setSelectedId(null); setDetail(null); }
    } catch {
      toast.error("Could not load jobs.");
    } finally {
      setLoading(false);
    }
  }, [applied, sort, selectedId]);

  useEffect(() => { fetchJobs(); }, [applied, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedId) return;
    setDetailLoading(true);
    api.get(`/jobs/${selectedId}`).then((r) => setDetail(r.data))
      .catch(() => setDetail(null))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  const search = () => setApplied({ ...draft });

  const selectCls = "h-11 rounded-lg border-0 bg-transparent px-2 text-sm text-slate-700 outline-none w-full";

  return (
    <div data-testid="discover-jobs-page" className="space-y-5">
      {/* Header banner */}
      <div className="rounded-2xl bg-gradient-to-r from-amber-50 via-white to-amber-100/60 border border-amber-100 px-6 py-7">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight font-display">Find Jobs That Match Your Career Goals</h1>
        <p className="text-slate-500 mt-1.5">Discover opportunities, connect with top companies, and take the next step in your career.</p>
      </div>

      {/* Filter bar */}
      <div className="rounded-2xl border border-slate-200 bg-white px-3 py-2.5 flex flex-col lg:flex-row gap-2 lg:items-center shadow-sm">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="jobs-search-input"
            value={draft.q}
            onChange={(e) => setDraft({ ...draft, q: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") search(); }}
            placeholder="Job title, skills or company"
            className="w-full h-11 pl-9 pr-3 rounded-lg text-sm text-slate-800 bg-transparent outline-none border-0 focus:ring-0 placeholder:text-slate-400"
          />
        </div>
        <div className="hidden lg:block w-px h-7 bg-slate-200" />
        <div className="flex items-center gap-1.5 min-w-[170px] px-2">
          <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
          <select data-testid="jobs-saved-filter" value={draft.company}
            onChange={(e) => setDraft({ ...draft, company: e.target.value })} className={selectCls}>
            <option value="">Saved Companies ({savedCompanies.length})</option>
            {savedCompanies.map((c) => <option key={c.id} value={c.client_name}>{c.client_name}</option>)}
          </select>
        </div>
        <div className="hidden lg:block w-px h-7 bg-slate-200" />
        <div className="flex items-center gap-1.5 min-w-[150px] px-2">
          <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
          <select data-testid="jobs-location-filter" value={draft.location}
            onChange={(e) => setDraft({ ...draft, location: e.target.value })} className={selectCls}>
            <option value="">All locations</option>
            {meta.locations.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div className="hidden lg:block w-px h-7 bg-slate-200" />
        <div className="flex items-center gap-1.5 min-w-[140px] px-2">
          <BarChart3 className="w-4 h-4 text-slate-400 shrink-0" />
          <select data-testid="jobs-experience-filter" value={draft.experience}
            onChange={(e) => setDraft({ ...draft, experience: e.target.value })} className={selectCls}>
            {EXPERIENCE_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="hidden lg:block w-px h-7 bg-slate-200" />
        <div className="flex items-center gap-1.5 min-w-[130px] px-2">
          <Briefcase className="w-4 h-4 text-slate-400 shrink-0" />
          <select data-testid="jobs-workmode-filter" value={draft.work_mode}
            onChange={(e) => setDraft({ ...draft, work_mode: e.target.value })} className={selectCls}>
            <option value="">Any work mode</option>
            {meta.work_modes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <Button data-testid="jobs-search-button" onClick={search}
          className="h-11 px-7 bg-amber-500 text-black hover:bg-amber-400 font-semibold rounded-lg transition-colors lg:ml-2">
          Search Jobs
        </Button>
      </div>

      {/* Popular searches */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500 mr-1">Popular searches:</span>
        {POPULAR.map((p) => (
          <button key={p} data-testid={`popular-${p.replace(/\W+/g, "-").toLowerCase()}`}
            onClick={() => { const d = { ...draft, q: p }; setDraft(d); setApplied(d); }}
            className="px-3.5 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-amber-300 hover:text-amber-700 transition-colors">
            {p}
          </button>
        ))}
      </div>

      {/* Split view */}
      <div className="grid lg:grid-cols-[420px_1fr] gap-5 items-start">
        {/* Jobs list */}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
            <h2 className="font-bold text-slate-900" data-testid="jobs-count">Jobs ({total})</h2>
            <select data-testid="jobs-sort" value={sort} onChange={(e) => setSort(e.target.value)}
              className="text-sm text-slate-600 bg-transparent outline-none border-0 cursor-pointer">
              <option value="recent">Sort by: Recent</option>
              <option value="oldest">Sort by: Oldest</option>
            </select>
          </div>
          <div className="max-h-[640px] overflow-y-auto divide-y divide-slate-100">
            {loading ? (
              <div className="py-16 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" /></div>
            ) : jobs.length === 0 ? (
              <div className="py-16 text-center text-slate-500 px-6">
                {savedCompanies.length === 0 ? (
                  <>
                    <Building2 className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="font-medium text-slate-700">No saved companies yet</p>
                    <p className="text-sm mt-1">Jobs appear here only from companies you save.</p>
                    <Link to="/app/companies" data-testid="save-companies-cta"
                      className="inline-block mt-4 px-5 py-2 rounded-lg bg-amber-500 text-black text-sm font-semibold hover:bg-amber-400 transition-colors">
                      Browse &amp; Save Companies
                    </Link>
                  </>
                ) : (
                  <>
                    <Search className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    <p className="font-medium text-slate-700">No jobs from your saved companies</p>
                    <p className="text-sm mt-1">Only jobs from saved companies with a verified HR contact are shown. Try broadening your filters.</p>
                  </>
                )}
              </div>
            ) : (
              jobs.map((j) => (
                <button
                  key={j.id}
                  data-testid={`job-card-${j.id}`}
                  onClick={() => setSelectedId(j.id)}
                  className={`w-full text-left px-5 py-4 transition-colors ${
                    selectedId === j.id ? "bg-amber-50/70 border-l-4 border-l-amber-500" : "hover:bg-slate-50 border-l-4 border-l-transparent"
                  }`}
                >
                  <div className="flex gap-3">
                    <CompanyLogo job={j} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold text-slate-900 leading-snug">
                          {j.title}
                          {j.is_new && <span className="ml-2 px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold align-middle">New</span>}
                        </p>
                        <span className="text-xs text-slate-400 whitespace-nowrap">{postedLabel(j.posted_days_ago)}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">{j.company}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {j.location}</span>
                        <span className="inline-flex items-center gap-1"><Briefcase className="w-3 h-3" /> {j.type}</span>
                        <span className="inline-flex items-center gap-1"><BarChart3 className="w-3 h-3" /> {expLabel(j)}</span>
                        {j.has_contact && (
                          <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><ShieldCheck className="w-3 h-3" /> Verified HR contact</span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Job detail */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 lg:sticky lg:top-24 min-h-[400px]" data-testid="job-detail-panel">
          {detailLoading ? (
            <div className="py-24 text-center"><Loader2 className="w-7 h-7 mx-auto animate-spin text-amber-500" /></div>
          ) : !detail ? (
            <div className="py-24 text-center text-slate-500">
              <Layers className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-700">Select a job to view details</p>
            </div>
          ) : (
            <>
              <div className="flex items-start gap-4">
                <CompanyLogo job={detail} size="w-12 h-12 text-xl" />
                <div className="min-w-0">
                  <h2 className="text-xl font-bold text-slate-900 leading-snug" data-testid="job-detail-title">
                    {detail.title}
                    {detail.is_new && <span className="ml-2 px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-[11px] font-bold align-middle">New</span>}
                  </h2>
                  <p className="text-slate-600 mt-0.5 flex items-center gap-1.5">
                    {detail.company}
                    <button
                      data-testid="employer-info-button"
                      onClick={() => openEmployer(detail.company)}
                      title="About the employer"
                      className="text-slate-400 hover:text-amber-600 transition-colors"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </p>
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2.5 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {detail.location}</span>
                    <span className="inline-flex items-center gap-1.5"><Briefcase className="w-4 h-4" /> {detail.type}</span>
                    <span className="inline-flex items-center gap-1.5"><BarChart3 className="w-4 h-4" /> {expLabel(detail)}</span>
                    <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4" /> Posted {postedLabel(detail.posted_days_ago)}</span>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <h3 className="font-bold text-slate-900">Job Description</h3>
                <p className="text-sm text-slate-600 leading-relaxed mt-2">{detail.description}</p>
              </div>

              {detail.responsibilities?.length > 0 && (
                <div className="mt-5">
                  <h3 className="font-bold text-slate-900">Key Responsibilities</h3>
                  <ul className="mt-2 space-y-1.5">
                    {detail.responsibilities.map((r, i) => (
                      <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-amber-500 mt-1">•</span> {r}</li>
                    ))}
                  </ul>
                </div>
              )}

              {detail.qualifications?.length > 0 && (
                <div className="mt-5">
                  <h3 className="font-bold text-slate-900">Required Qualifications</h3>
                  <ul className="mt-2 space-y-1.5">
                    {detail.qualifications.map((q, i) => (
                      <li key={i} className="text-sm text-slate-600 flex gap-2"><span className="text-amber-500 mt-1">•</span> {q}</li>
                    ))}
                  </ul>
                </div>
              )}

              <Button
                data-testid="job-apply-now"
                onClick={() => setApplyOpen(true)}
                className="mt-7 h-12 px-10 bg-amber-500 text-black hover:bg-amber-400 font-bold rounded-lg transition-colors"
              >
                Apply Now
              </Button>
            </>
          )}
        </div>
      </div>

      {applyOpen && detail && (
        <ApplyModal job={detail} onClose={(applied_) => { setApplyOpen(false); if (applied_) fetchJobs(); }} />
      )}

      {/* Employer info slide-over */}
      <div
        data-testid="employer-panel"
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-[360px] bg-white shadow-2xl border-l border-slate-200 transform transition-transform duration-300 ease-out ${
          employerOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900">About the Employer</h2>
          <button data-testid="employer-close" onClick={() => setEmployerOpen(false)}
            className="text-slate-400 hover:text-slate-700 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        {employerLoading ? (
          <div className="py-20 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin text-amber-500" /></div>
        ) : employer ? (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <CompanyLogo job={{ company: employer.name, domain: employer.domain }} size="w-12 h-12 text-xl" />
              <p className="font-bold text-slate-900 leading-snug" data-testid="employer-name">{employer.name}</p>
            </div>
            <div className="rounded-xl border border-slate-100 divide-y divide-slate-100">
              {employer.industry && (
                <div className="flex items-center gap-3 px-4 py-3" data-testid="employer-industry">
                  <BarChart3 className="w-4 h-4 text-slate-400 shrink-0" />
                  <div><p className="text-xs text-slate-400">Industry</p><p className="text-sm font-medium text-slate-900">{employer.industry}</p></div>
                </div>
              )}
              {employer.size && (
                <div className="flex items-center gap-3 px-4 py-3" data-testid="employer-size">
                  <Users className="w-4 h-4 text-slate-400 shrink-0" />
                  <div><p className="text-xs text-slate-400">Company Size</p><p className="text-sm font-medium text-slate-900">{employer.size}</p></div>
                </div>
              )}
              {employer.headquarters && (
                <div className="flex items-center gap-3 px-4 py-3" data-testid="employer-hq">
                  <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                  <div><p className="text-xs text-slate-400">Headquarters</p><p className="text-sm font-medium text-slate-900">{employer.headquarters}</p></div>
                </div>
              )}
              {employer.website && (
                <div className="flex items-center gap-3 px-4 py-3" data-testid="employer-website">
                  <Globe className="w-4 h-4 text-slate-400 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">Website</p>
                    <a href={employer.website} target="_blank" rel="noreferrer"
                      className="text-sm font-medium text-amber-600 hover:underline break-all">
                      {employer.website.replace(/^https?:\/\//, "")}
                    </a>
                  </div>
                </div>
              )}
              {!employer.industry && !employer.size && !employer.headquarters && !employer.website && (
                <p className="px-4 py-6 text-sm text-slate-400 text-center">No additional employer details available yet.</p>
              )}
            </div>
          </div>
        ) : null}
      </div>
      {employerOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/30" onClick={() => setEmployerOpen(false)} data-testid="employer-backdrop" />
      )}
    </div>
  );
}
